import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import { AlertTriangle, Search, X } from 'lucide-react';

/**
 * SSO Attempts Log tab: last 50 SSO login attempts with rule-evaluation breakdown.
 * Self-loads on mount (parent conditionally mounts this only when the tab is active).
 */
export default function SsoAttemptsLogTab() {
  const [ssoAttempts, setSsoAttempts]         = useState([]);
  const [loadingAttempts, setLoadingAttempts] = useState(false);
  const [searchQuery, setSearchQuery]         = useState('');

  const loadSsoAttempts = useCallback(async () => {
    setLoadingAttempts(true);
    try {
      const data = await api.request('/admin/access-rules/sso-attempts');
      setSsoAttempts(data);
    } catch { /* ignore */ }
    finally { setLoadingAttempts(false); }
  }, []);

  useEffect(() => { loadSsoAttempts(); }, [loadSsoAttempts]);

  const filteredAttempts = ssoAttempts.filter(attempt => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.trim().toLowerCase();
    const domain = (attempt.token_props?.domain || attempt.email?.split('@')[1] || '');
    return attempt.email?.toLowerCase().includes(q) || domain.toLowerCase().includes(q);
  });

  return (
    <div data-testid="sso-log-tab">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <p className="text-sm text-gray-500 flex-1 min-w-[240px]">
          Last 50 SSO login attempts. Use this to see the exact token values sent by LevelUp for specific users and diagnose why rules match or fail.
        </p>
        <div className="flex items-center gap-2">
          <div className="relative w-56">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by email or domain..."
              className="w-full pl-8 pr-7 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-levelup-accent/40 focus:border-levelup-accent"
              data-testid="sso-log-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="sso-log-search-clear"
              >
                <X size={13} />
              </button>
            )}
          </div>
          <button onClick={loadSsoAttempts} disabled={loadingAttempts}
            className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 whitespace-nowrap">
            {loadingAttempts ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      {ssoAttempts.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
          <AlertTriangle size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No SSO attempts logged yet.</p>
          <p className="text-gray-400 text-sm mt-1">Attempts will appear here after users try to sign in via LevelUp SSO.</p>
        </div>
      ) : filteredAttempts.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl" data-testid="sso-log-no-results">
          <Search size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No attempts match "{searchQuery}"</p>
          <p className="text-gray-400 text-sm mt-1">Try a different email or domain.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredAttempts.map(attempt => {
            // Failed token-exchange attempts (e.g. "token_expired") log a different
            // shape than successful ones: token_props = { attempts: [{url, status}], used_url }
            // instead of flat fields like role_name/domain_type. Render each shape safely.
            const rawProps = attempt.token_props || {};
            const exchangeAttempts = Array.isArray(rawProps.attempts) ? rawProps.attempts : null;
            const flatProps = Object.entries(rawProps).filter(
              ([k, v]) => k !== 'domain' && k !== 'attempts' && (v === null || typeof v !== 'object')
            );
            return (
            <div key={attempt.id} data-testid={`sso-attempt-${attempt.id}`}
              className={`border rounded-xl overflow-hidden ${attempt.allowed ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-center gap-3 p-3">
                <span className={`text-xs font-bold px-2 py-1 rounded ${attempt.allowed ? 'bg-emerald-200 text-emerald-800' : 'bg-red-200 text-red-800'}`}>
                  {attempt.allowed ? 'ALLOWED' : 'DENIED'}
                </span>
                <span className="font-medium text-gray-800 text-sm break-all">{attempt.email}</span>
                <span className="text-xs text-gray-400 ml-auto whitespace-nowrap">{attempt.attempted_at ? new Date(attempt.attempted_at).toLocaleString() : ''}</span>
              </div>
              {attempt.reason && (
                <div className="px-3 pb-1 text-xs text-gray-600">
                  <span className="font-semibold">Reason: </span>{attempt.reason}
                </div>
              )}
              {flatProps.length > 0 && (
                <div className="px-3 pb-3 grid grid-cols-2 gap-x-4 gap-y-1">
                  {flatProps.map(([k, v]) => (
                    <div key={k} className="flex items-center gap-1 text-xs">
                      <span className="text-gray-500 w-24 flex-shrink-0">{k}:</span>
                      <span className="font-mono bg-white border border-gray-200 px-1.5 py-0.5 rounded text-gray-700 truncate">{v || '(empty)'}</span>
                    </div>
                  ))}
                </div>
              )}
              {exchangeAttempts && exchangeAttempts.length > 0 && (
                <div className="px-3 pb-3">
                  <p className="text-xs text-gray-500 mb-1">LevelUp endpoints attempted:</p>
                  <div className="space-y-1">
                    {exchangeAttempts.map((a, i) => (
                      <div key={i} data-testid={`sso-attempt-${attempt.id}-exchange-${i}`} className="flex items-center gap-2 text-xs">
                        <span className={`font-mono px-1.5 py-0.5 rounded ${a.status === 200 ? 'bg-emerald-100 text-emerald-700' : 'bg-white border border-gray-200 text-gray-700'}`}>
                          {a.status ?? 'unreachable'}
                        </span>
                        <span className="font-mono text-gray-500 truncate">{a.url || '(unknown url)'}</span>
                        {a.error && <span className="text-red-500 truncate">{a.error}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!attempt.allowed && attempt.rule_breakdown?.length > 0 && (
                <div className="border-t border-red-200 px-3 py-2 bg-white">
                  <p className="text-xs font-semibold text-gray-600 mb-1">Rule Evaluation:</p>
                  {attempt.rule_breakdown.map((rb, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                      <span className={rb.matched ? 'text-emerald-600' : 'text-gray-400'}>{rb.matched ? '✓' : '✗'}</span>
                      <span className="text-gray-700 font-medium">{rb.rule_name}</span>
                      <span className={`text-xs px-1 rounded ${rb.effect === 'ALLOW' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{rb.effect}</span>
                      {rb.conditions?.filter(c => !c.result).map((c, j) => (
                        <span key={j} className="text-red-500 font-mono text-xs">
                          {c.field} {c.operator} "{c.value}" (got: "{c.token_value}")
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
